import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import Button from "@material-ui/core/Button";
import sortBy from "lodash/sortBy";
import Dialog from "@material-ui/core/Dialog";

import { Flex, PromptHeader, PromptBody, PromptButton, PrimaryHeader } from "../styles/global";
import { getInterventions, deleteIntervention } from "../utils/api.utils";
import { InterventionEditor } from "../pages/InterventionEditor";
import { Intervention } from "../models";
import { useIsComponentMounted } from '../utils/hooks.utils'
import defaultIntervention from "../data/defaultIntervention";
import interventionIcon from "../assets/intervention.svg";

export const InterventionList = () => {
  const [showEditScreen, setEditScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentIntervention, setCurrentIntervention] = useState<any>();
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isComponentMounted = useIsComponentMounted();

  const fetchInterventions = useCallback(() => {
    getInterventions()
      .then((result: any) => {
        if (isComponentMounted.current) {
          setLoading(false)
          const payload = result as any;
          setInterventions(sortBy(payload, "created_at"));
        }
      })
      .catch((err) => {
        if (isComponentMounted.current) {
          setInterventions([])
          setLoading(false)
        }
      });
  }, [isComponentMounted]);

  const createNewIntervention = () => {
    setCurrentIntervention(defaultIntervention);
    setEditScreen(true);
  };

  const selectIntervention = (intervention: any) => {
    setCurrentIntervention(intervention);
    setEditScreen(true);
  };

  const handleInterventionDelete = (intervention: any) => {
    deleteIntervention(intervention.id)
      .then((res) => console.log(res))
      .catch((err) => console.error(err));
    setCurrentIntervention(undefined);
    setShowDeleteModal(false);
    setTimeout(() => fetchInterventions(), 1000);
    fetchInterventions();
  };

  const displayDeleteModal = (intervention: any) => {
    if (!intervention) {
      return;
    }
    setCurrentIntervention(intervention);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    fetchInterventions()
  }, [fetchInterventions]);

  if (showEditScreen) {
    window.scrollTo(0, 0);
    return (
      <InterventionEditor
        shouldDisplay={setEditScreen}
        fetchInterventions={fetchInterventions}
        intervention={currentIntervention}
      />
    );
  }
  return (
    <div style={{ padding: '0 0 24px 0', width: '100%', boxSizing: 'border-box' }}>
      <Flex justify="space-between" alignItems="center" style={{ marginBottom: '32px' }}>
        <PrimaryHeader style={{ margin: 0 }}>Interventions</PrimaryHeader>
        <Button
          variant="contained"
          color="primary"
          style={{ borderRadius: '8px', padding: '10px 24px', fontWeight: 600, boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)' }}
          onClick={() => createNewIntervention()}>
          + New Intervention
        </Button>
      </Flex>

      <div id="interventions-view">
        {!loading ?
          interventions && interventions.length >= 1 ?
            interventions.map((item: any) => (
              <InterventionCard
                key={item.id + item.name}
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <InterventionInfo direction="row" alignItems="center" style={{ flex: 1 }}>
                  <IconContainer>
                    <img src={interventionIcon} alt="Intervention" />
                  </IconContainer>
                  <InterventionTextContent direction="column">
                    <Flex alignItems="center" style={{ marginBottom: '4px' }}>
                      <InterventionTitle>{item.name || "Untitled Intervention"}</InterventionTitle>
                    </Flex>
                    <Flex wrap="wrap" alignItems="center">
                      <StatPill>ID: {item.id}</StatPill>
                    </Flex>
                  </InterventionTextContent>
                </InterventionInfo>

                <ActionGroup alignItems="center">
                  <ActionButton
                    variant="outlined"
                    onClick={() => selectIntervention(item)}
                  >
                    Edit
                  </ActionButton>
                  <DeleteButton onClick={(e) => {
                    (e.currentTarget as HTMLElement).blur();
                    displayDeleteModal(item);
                  }}>
                    Delete
                  </DeleteButton>
                </ActionGroup>
              </InterventionCard>
            ))
            :
            <NotFound>No Interventions Found</NotFound>
          :
          <NotFound>Loading...</NotFound>
        }

        <Dialog
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          PaperProps={{
            style: { borderRadius: '16px', padding: '8px', maxWidth: '450px' }
          }}
        >
          <ModalContent direction="column" alignItems="center">
            <div className="icon-container delete">!</div>
            <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
              Delete Intervention?
            </PromptHeader>
            <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
              Are you sure you want to delete this intervention? All data associated with it will be permanently removed.
            </PromptBody>
            <Flex style={{ marginTop: '32px', width: '100%', gap: '12px' }} justify="center">
              <PromptButton
                autoFocus
                onClick={() => setShowDeleteModal(false)}
                variant="outlined"
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  borderColor: '#e2e8f0'
                }}
              >
                Cancel
              </PromptButton>
              <PromptButton
                onClick={() => handleInterventionDelete(currentIntervention)}
                variant="contained"
                style={{
                  background: '#c10000',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  boxShadow: '0 4px 12px rgba(193, 0, 0, 0.2)'
                }}
              >
                Delete
              </PromptButton>
            </Flex>
          </ModalContent>
        </Dialog>
      </div>
    </div>
  );
};

const ModalContent = styled(Flex)`
  padding: 32px;
  
  .icon-container {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 20px;
    
    &.delete {
      background: #fff5f5;
      color: #e53e3e;
      border: 2px solid #feb2b2;
    }
  }
`;

const InterventionCard = styled(Flex)`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.15);
    border-color: #cbd5e0;
  }
`;

const IconContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #e0f2f1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  flex-shrink: 0;

  img {
    width: 28px;
    height: 28px;
    opacity: 0.8;
  }
`;

const InterventionInfo = styled(Flex)``;

const InterventionTextContent = styled(Flex)`
  flex: 1;
`;

const InterventionTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  margin-right: 12px;
`;

const StatPill = styled.span`
  font-size: 13px;
  color: #718096;
  background: #f7fafc;
  padding: 2px 10px;
  border-radius: 20px;
  margin-right: 12px;
  border: 1px solid #edf2f7;
`;


const ActionGroup = styled(Flex)`
  margin-left: 24px;
  gap: 12px;
`;

const ActionButton = styled(Button)`
  && {
    min-width: 100px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    border-radius: 8px;
    border-color: #e2e8f0;
    color: #4a5568;
    &:hover {
      background: #f7fafc;
      border-color: #cbd5e0;
    }
  }
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 80px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    color: #e53e3e;
    &:hover {
      background: #fff5f5;
    }
  }
`;

const NotFound = styled.h4`
  font-weight: 600;
  margin-top: 80px;
  font-size: 20px;
  color: #718096;
  text-align: center;
`;